import type { User } from "@lib/types";
import { CalendarClock } from "lucide-react";
import { useEffect, useState } from "react";

interface Counts {
  due: number;
  overdue: number;
}

// Persistent compliance reminder for superusers (SOC 2 plan WS-6): a slim
// strip showing how many compliance tasks are open, escalating to red when
// anything is overdue. Links into the admin compliance calendar.
export function ComplianceIndicator({ currentUser }: { currentUser: User }) {
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    if (!currentUser.superuser) return;
    let cancelled = false;
    fetch("/admin/compliance_occurrences/counts", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data) setCounts(data);
      })
      .catch((error) => console.error("Compliance counts fetch failed", error));
    return () => {
      cancelled = true;
    };
  }, [currentUser.superuser]);

  if (!currentUser.superuser || !counts) return null;
  const open = counts.due + counts.overdue;
  if (open === 0) return null;

  const hasOverdue = counts.overdue > 0;
  const tone = hasOverdue
    ? "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100"
    : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100";

  return (
    <a
      href="/admin/compliance_occurrences"
      title="Compliance calendar"
      className={`flex items-center gap-2 border-b px-6 py-1.5 text-xs font-medium hover:underline ${tone}`}
    >
      <CalendarClock size={14} aria-hidden="true" />
      <span>
        {hasOverdue
          ? `${counts.overdue} compliance task${counts.overdue === 1 ? "" : "s"} overdue — review now`
          : `${open} compliance task${open === 1 ? "" : "s"} due`}
      </span>
    </a>
  );
}
